import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ColumnFilterPanel from '@/components/CustomizableTable/ColumnFilterPanel';
import ColumnFilterControls from '@/components/CustomizableTable/ColumnFilterControls';
import { EXCEPTION_TYPES, EXCEPTION_DISPLAY } from '@onward-delivery/core';

const OCCURRED_AT_OPTIONS = [
    { value: 'DROPOFF', label: 'DROPOFF' },
    { value: 'PICKUP', label: 'PICKUP' },
];

export const COLUMN_FILTER_MAP = {
    exception_time: {
        title: 'Reported Date',
        controls: [{ type: 'dateRange', keys: ['start', 'end'], label: 'Reported Between' }],
    },
    exception_type: {
        title: 'Exception Type',
        controls: [
            {
                type: 'select',
                key: 'type',
                label: 'Exception Type',
                options: [
                    { value: '', label: 'All' },
                    ...Object.values(EXCEPTION_TYPES).map((typeVal) => ({
                        value: typeVal,
                        label: EXCEPTION_DISPLAY[typeVal] || typeVal,
                    })),
                ],
            },
        ],
    },
    exception_occured_at: {
        title: 'Occurred At',
        controls: [
            {
                type: 'select',
                key: 'occurred_at',
                label: 'Occurred At',
                options: [{ value: '', label: 'All' }, ...OCCURRED_AT_OPTIONS],
            },
        ],
    },
    shipper_name: {
        title: 'Shipper',
        controls: [{ type: 'multiSelect', key: 'shippers', label: 'Shipper', dynamicOptions: 'shippers' }],
    },
    carrier_name: {
        title: 'Carrier',
        controls: [{ type: 'multiSelect', key: 'carriers', label: 'Carrier', dynamicOptions: 'carriers' }],
    },
};

const AdminExceptionsColumnFilterPanel = ({
    open,
    columnId,
    onClose,
    applyFilters,
    filter,
    shippers = [],
    carriers = [],
}) => {
    const [localFilters, setLocalFilters] = useState({});

    const config = COLUMN_FILTER_MAP[columnId];

    useEffect(() => {
        if (!open || !config) return;

        const next = {};
        config.controls.forEach((control) => {
            if (control.type === 'dateRange' && Array.isArray(control.keys)) {
                control.keys.forEach((key) => {
                    const raw = filter[key];
                    next[key] = raw && raw.toISOString ? raw.toISOString().slice(0, 10) : '';
                });
            } else if (control.type === 'select' && control.key) {
                next[control.key] = filter[control.key] || '';
            } else if (control.type === 'multiSelect' && control.key) {
                next[control.key] = Array.isArray(filter[control.key]) ? filter[control.key] : [];
            }
        });

        setLocalFilters(next);
    }, [open, config, filter]);

    const setValue = (key, value) => {
        setLocalFilters((prev) => ({ ...prev, [key]: value }));
    };

    const resolveOptions = useCallback(
        (control) => {
            if (control.dynamicOptions === 'shippers') {
                return shippers.map((s) => ({ value: s.value, label: s.label }));
            }
            if (control.dynamicOptions === 'carriers') {
                return carriers.map((c) => ({ value: c.value, label: c.label }));
            }
            return control.options || [];
        },
        [shippers, carriers]
    );

    if (!config) return null;

    const handleApply = () => {
        const payload = {};
        config.controls.forEach((control) => {
            if (control.type === 'dateRange') {
                control.keys.forEach((key) => {
                    const val = localFilters[key];
                    payload[key] = val ? new Date(val) : null;
                });
            } else if (control.type === 'select') {
                const val = localFilters[control.key];
                payload[control.key] = val || null;
            } else if (control.type === 'multiSelect') {
                const val = localFilters[control.key];
                payload[control.key] = Array.isArray(val) ? val : val ? [val] : [];
            }
        });

        applyFilters(payload);
    };

    const handleClear = () => {
        const payload = {};
        config.controls.forEach((control) => {
            if (control.type === 'dateRange') {
                control.keys.forEach((key) => {
                    payload[key] = null;
                });
            } else if (control.type === 'select') {
                payload[control.key] = null;
            } else if (control.type === 'multiSelect') {
                payload[control.key] = [];
            }
        });
        setLocalFilters((prev) => {
            const cleared = { ...prev };
            config.controls.forEach((control) => {
                if (control.type === 'dateRange') {
                    control.keys.forEach((key) => {
                        cleared[key] = '';
                    });
                } else if (control.type === 'select') {
                    cleared[control.key] = '';
                } else if (control.type === 'multiSelect') {
                    cleared[control.key] = [];
                }
            });
            return cleared;
        });

        applyFilters(payload);
    };

    return (
        <ColumnFilterPanel
            open={open}
            columnId={columnId}
            title={config.title}
            onClose={onClose}
            onApply={handleApply}
            onClear={handleClear}
            topOffset={50}
        >
            <ColumnFilterControls
                controls={config.controls}
                values={localFilters}
                setValue={setValue}
                resolveOptions={resolveOptions}
            />
        </ColumnFilterPanel>
    );
};

export default AdminExceptionsColumnFilterPanel;
